// components/SubscribeModal.jsx
import React, { useState, useEffect } from "react";
import { useSubscriptionStore } from "../store/subscriptionStore";
import { toast } from "react-toastify";
import {
  BiX,
  BiDollar,
  BiPackage,
  BiCheckCircle,
  BiUser,
  BiPhone,
  BiEnvelope,
} from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../functions/formatPhoneNumberFunctions.js";
import useAuthStore from "../store/authStore";

const SubscribeModal = ({ plan, isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { subscribeToPlan, subscribeToPlanAnonyme, loading } =
    useSubscriptionStore();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [fedapayLoaded, setFedapayLoaded] = useState(false);
  const [name, setName] = useState("");
  const [prenom, setPrenom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");

  // Pré-remplir avec les données utilisateur si disponible
  useEffect(() => {
    if (user) {
      if (user.nom) setName(user.nom);
      if (user.prenom) setPrenom(user.prenom);
      if (user.email) setEmail(user.email);
      if (user.telephone) setTelephone(user.telephone);
    }
  }, [user]);

  // Charger FedaPay
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.fedapay.com/checkout.js?v=1.1.7";
    script.onload = () => setFedapayLoaded(true);
    script.onerror = () => toast.error("Erreur de chargement de FedaPay");
    document.body.appendChild(script);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setFedapayLoaded(false);
      setIsProcessingPayment(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Fonction pour initialiser le paiement FedaPay
  const initializeFedapayPayment = async () => {
    try {
      setIsProcessingPayment(true);

      // Validation basique
      if (!name.trim()) {
        toast.error("Le nom est requis");
        setIsProcessingPayment(false);
        return;
      }
      if (!telephone.trim()) {
        toast.error("Le téléphone est requis");
        setIsProcessingPayment(false);
        return;
      }

      const descriptionLabel = `Souscription ${plan.name}`;

      if (!fedapayLoaded || !window.FedaPay) {
        toast.error("FedaPay n'est pas encore prêt. Réessayez.");
        setIsProcessingPayment(false);
        return;
      }

      // Initialiser FedaPay Checkout
      const newWidget = window.FedaPay.init({
        public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY,
        transaction: {
          amount: parseInt(plan.price.toString()),
          description: descriptionLabel,
        },
        customer: {
          email: email || user?.email,
          lastname: name || user?.nom,
          firstname: prenom || user?.prenom || "",
          phone_number:
            formatPhoneNumber(telephone) || formatPhoneNumber(user?.telephone),
        },
        ui: {
          theme: "light",
          display_mode: "popup",
        },
        onComplete: async ({ reason, transaction }) => {
          if (transaction && transaction.status !== "approved") {
            toast.error(reason);
            setIsProcessingPayment(false);
          } else {
            await handlePaymentSuccess(transaction);
            setIsProcessingPayment(false);
          }
        },
      });

      // Ouvrir la fenêtre FedaPay
      await newWidget.open();
    } catch (error) {
      console.error("Erreur d'initialisation FedaPay :", error);
      toast.error("Erreur FedaPay : " + error.message);
      setIsProcessingPayment(false);
    }
  };

  // Gérer le succès du paiement
  const handlePaymentSuccess = async (result) => {
    try {
      const transactionId = result.id;

      // Créer la souscription avec statut payé
      let subscriptionData;
      if (user) {
        subscriptionData = {
          planId: plan.id,
          transaction_id: transactionId,
        };
        console.log("connected user");
        await subscribeToPlan(subscriptionData);
      } else {
        subscriptionData = {
          planId: plan.id,
          transaction_id: transactionId,
          name: `${name} ${prenom}`,
          email: email,
          telephone: telephone,
        };
        console.log("anonymous");
        await subscribeToPlanAnonyme(subscriptionData);
      }

      toast.success("Paiement et souscription effectués avec succès !");

      // Fermer le modal et réinitialiser
      onClose();

      // Rediriger vers la page des abonnements
      setTimeout(() => {
        navigate("/subscriptions");
      }, 1500);
    } catch (error) {
      console.error("Erreur création souscription après paiement:", error);
      toast.error("Paiement réussi mais erreur de création de la souscription");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XAF",
    }).format(price);
  };

  const isUserConnected = !!user;
  const isNameDisabled = isUserConnected && user.nom;
  const isPrenomDisabled = isUserConnected && user.prenom;
  const isEmailDisabled = isUserConnected && user.email;
  const isPhoneDisabled = isUserConnected && user.telephone;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white w-full max-w-lg h-[80vh] flex flex-col shadow-2xl overflow-hidden">
        {/* En-tête fixe */}
        <div className="flex-none p-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-secondary to-secondary-700 flex items-center justify-center">
              <BiPackage className="text-white text-sm" />
            </div>
            <div className="min-w-0">
              <h2 className="font-poppins font-bold text-[15px] text-gray-800 truncate">
                Souscrire à {plan.name}
              </h2>
              <p className="font-nunito font-light text-[12px] text-gray-500 truncate">
                Finalisez votre souscription
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 transition-all duration-200"
          >
            <BiX size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Informations du plan */}
          <div className="p-3 bg-gray-50 border border-gray-100">
            <h3 className="font-poppins font-bold text-[14px] text-gray-700 mb-1">
              Détails du plan
            </h3>
            <p className="font-nunito font-light text-[13px] text-gray-600 mb-2">
              {plan.description}
            </p>
            <p className="font-poppins font-bold text-[16px] text-secondary">
              {formatPrice(plan.price)}
            </p>
          </div>

          {/* Services inclus */}
          {plan.includedServices && plan.includedServices.length > 0 && (
            <div>
              <h4 className="font-poppins font-bold text-[14px] text-gray-700 mb-2 flex items-center gap-2">
                <BiCheckCircle className="text-green-500 text-base" />
                Services inclus
              </h4>
              <div className="space-y-1">
                {plan.includedServices.map((includedService, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-2 bg-white border border-gray-50"
                  >
                    <span className="font-nunito font-light text-[13px] text-gray-700">
                      {includedService.service?.nom}
                    </span>
                    <span className="font-poppins font-bold text-[12px] bg-secondary/10 text-secondary-700 px-2 py-0.5">
                      ×{includedService.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulaire */}
          <div>
            <h4 className="font-poppins font-bold text-[14px] text-gray-700 mb-3 flex items-center gap-2">
              <BiUser className="text-secondary text-base" />
              Informations personnelles
            </h4>

            {isUserConnected && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-100">
                <p className="font-nunito font-light text-[12px] text-blue-700">
                  <span className="font-bold">Note :</span> Champs pré-remplis avec vos données.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block font-nunito font-bold text-[11px] text-gray-500 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isNameDisabled}
                  className={`w-full px-3 py-2 border rounded font-nunito text-sm transition-all ${isNameDisabled
                      ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-800 focus:border-secondary"
                    }`}
                  placeholder="Votre nom"
                  required
                />
              </div>

              <div>
                <label className="block font-nunito font-bold text-[11px] text-gray-500 mb-1">
                  Prénom *
                </label>
                <input
                  type="text"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  disabled={isPrenomDisabled}
                  className={`w-full px-3 py-2 border rounded font-nunito text-sm transition-all ${isPrenomDisabled
                      ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-800 focus:border-secondary"
                    }`}
                  placeholder="Votre prénom"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block font-nunito font-bold text-[11px] text-gray-500 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isEmailDisabled}
                  className={`w-full px-3 py-2 border rounded font-nunito text-sm transition-all ${isEmailDisabled
                      ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-800 focus:border-secondary"
                    }`}
                  placeholder="votre@email.com"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block font-nunito font-bold text-[11px] text-gray-500 mb-1">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  disabled={isPhoneDisabled}
                  className={`w-full px-3 py-2 border rounded font-nunito text-sm transition-all ${isPhoneDisabled
                      ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-800 focus:border-secondary"
                    }`}
                  placeholder="+229 XX XX XX XX"
                  required
                />
              </div>
            </div>
          </div>

          {/* Total */}
          <div className="p-3 bg-gradient-to-r from-secondary-50 to-secondary-100 border border-secondary-200">
            <div className="flex justify-between items-center">
              <div>
                <span className="font-poppins font-bold text-[13px] text-gray-800">
                  Total à payer
                </span>
                <p className="font-nunito font-light text-[11px] text-gray-500">
                  Via FedaPay sécurisé
                </p>
              </div>
              <div className="text-right">
                <span className="font-poppins font-bold text-[18px] text-secondary">
                  {formatPrice(plan.price)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pied fixe */}
        <div className="flex-none p-3 border-t bg-gray-50">
          <div className="flex gap-2">
            <button
              onClick={initializeFedapayPayment}
              disabled={loading || isProcessingPayment || !name.trim() || !telephone.trim()}
              className="flex-[2] py-3 bg-gradient-to-r from-secondary to-secondary-700 text-white font-nunito font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
            >
              {loading || isProcessingPayment ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <BiDollar size={18} />
                  Payer
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 font-nunito font-bold text-sm text-gray-600 hover:bg-white transition-all"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribeModal;
